import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { PMField } from "../../../utils/PMField";
import ModernProductSelectorDialog from "../../../utils/Selector/ModernProductSelectorDialog";
import { PMCustomSelector } from "../../../utils/PMCustomSelector";
import { DeliveryAddressSelector } from "../../../utils/moduleSelectors/deliveryAddressSelector";

/** Small uppercase heading that groups related fields (no per-field cards). */
function FormSection({ title, children }) {
  return (
    <section className="space-y-3">
      <h4 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h4>
      {children}
    </section>
  );
}

const FIELD_GRID = "grid grid-cols-1 gap-4 sm:grid-cols-2";

export default function SystemForm(props) {
  const { formik, systemTypes, readOnly } = props;
  const { t } = useTranslation();

  const [productSelectorOpen, setProductSelectorOpen] = useState(false);
  const productSelectorFilters = useMemo(
    () => [{ name: "hasSpareParts", value: true }],
    []
  );

  const handleSystemTypeChange = (systemType) => {
    if (systemType) {
      formik.setFieldValue("system_type_id", systemType.id);
      formik.setFieldValue("system_type", systemType);
    } else {
      formik.setFieldValue("system_type_id", null);
      formik.setFieldValue("system_type", null);
    }
  };

  const product = formik.values.product;
  const productLabel = product?.title
    ? `${product.item_number ? `${product.item_number} — ` : ""}${product.title}`
    : null;

  return (
    <div className="space-y-6">
      <FormSection title={t("customer.systems.form.equipment")}>
        <div className={FIELD_GRID}>
          <PMCustomSelector
            name="system_type_id"
            label={t("System Type")}
            title={t("Select System Type")}
            description={t("Choose the type of system for this customer")}
            field="title"
            field2="description"
            field3="code"
            size="lg"
            dataSource={systemTypes}
            selected={formik.values.system_type}
            onChange={(data) => handleSystemTypeChange(data.target.data)}
            {...formik}
          />

          <div>
            <label className="mb-1 block text-sm font-semibold text-slate-700">
              {t("Product")}
            </label>
            {/* Whole control is a button: 44px touch target, keyboard reachable */}
            <div
              className={`flex min-h-[44px] w-full items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 pl-3 pr-1.5 text-sm ${
                readOnly ? "opacity-80" : ""
              }`}
            >
              <button
                type="button"
                disabled={readOnly}
                onClick={() => setProductSelectorOpen(true)}
                className="flex min-w-0 flex-1 items-center gap-2 py-2 text-left disabled:cursor-default"
              >
                {productLabel ? (
                  <span className="truncate font-medium text-slate-900">
                    {productLabel}
                  </span>
                ) : (
                  <>
                    <MagnifyingGlassIcon className="h-4 w-4 shrink-0 text-slate-400" />
                    <span className="text-slate-400">
                      {t("customer.systems.select_product")}
                    </span>
                  </>
                )}
              </button>
              {productLabel && !readOnly ? (
                <button
                  type="button"
                  className="shrink-0 rounded-lg p-2 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                  onClick={() => {
                    formik.setFieldValue("product_id", null);
                    formik.setFieldValue("product", null);
                  }}
                  aria-label={t("Clear")}
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              ) : null}
            </div>
            {formik.errors.product_id ? (
              <div className="mt-1 text-xs text-red-500">
                {formik.errors.product_id}
              </div>
            ) : null}
          </div>

          <PMField
            name="system_m_name"
            type="text"
            label={t("Model name")}
            readOnly={readOnly}
            {...formik}
          />
          <PMField
            name="serial_number"
            type="text"
            label={t("Serial Number")}
            readOnly={readOnly}
            {...formik}
          />
        </div>
      </FormSection>

      <FormSection title={t("customer.systems.form.location")}>
        <div className={FIELD_GRID}>
          <PMField
            name="placement"
            type="text"
            label={t("Placement")}
            readOnly={readOnly}
            {...formik}
          />
          <DeliveryAddressSelector
            name="delivery_address_id"
            selected={formik.values.delivery_address}
            disabled={readOnly}
            {...formik}
          />
        </div>
      </FormSection>

      <FormSection title={t("customer.systems.form.dates")}>
        {/* Years are short — keep two columns even on phones */}
        <div className="grid grid-cols-2 gap-4">
          <PMField
            name="installation_year"
            type="number"
            label={t("Installation Year")}
            readOnly={readOnly}
            {...formik}
          />
          <PMField
            name="production_year"
            type="number"
            label={t("Production Year")}
            readOnly={readOnly}
            {...formik}
          />
        </div>
      </FormSection>

      <FormSection title={t("customer.systems.form.notes")}>
        <PMField
          name="other_comment"
          type="textarea"
          label={t("customer.systems.other_comment")}
          readOnly={readOnly}
          {...formik}
        />
      </FormSection>

      <ModernProductSelectorDialog
        multiple={false}
        open={productSelectorOpen}
        selectedProducts={product ? [product] : []}
        onClose={() => setProductSelectorOpen(false)}
        onChange={(data) => {
          if (!readOnly && data && data.length > 0) {
            const picked = data[0];
            formik.setFieldValue("product_id", picked.id);
            formik.setFieldValue("product", picked);
            if (!formik.values.system_m_name) {
              formik.setFieldValue("system_m_name", picked.title);
            }
          }
        }}
        defaultShowActiveOnly={false}
        defaultShowExpired={true}
        searchPlaceholder={t("customer.system.product_search_placeholder")}
        filters={productSelectorFilters}
      />
    </div>
  );
}
